import { post, get } from 'aws-amplify/api';
import { logger } from 'src/logger';
import {
  SearchRequestBody,
  FintechPapiSearchFilterOptions,
  FintechPapiResponse,
  FintechPapiEmployeeResponse,
  FintechPapiPersonInfo
} from './interfaces';

/**
 * Checks if an error is an AbortError and throws if so.
 * This allows proper cancellation propagation.
 */
const checkAbortSignal = (signal?: AbortSignal): void => {
  if (signal?.aborted) {
    const error = new Error('Request was cancelled');
    error.name = 'AbortError';
    throw error;
  }
};

/**
 * Races a shared promise against a caller's AbortSignal WITHOUT cancelling the shared
 * promise. If the signal aborts first, this caller rejects with an AbortError; the
 * underlying shared request keeps running (so it still populates the cache and serves
 * other/late sharers). If no signal is provided, the shared promise is returned unchanged
 * — identical behavior for the many callers that pass no signal.
 *
 * This is the crux of the deduplication fix: a request shared across callers must never be
 * torn down by any single participant's abort. Per-caller cancellation is layered here,
 * on top of a signal-agnostic shared request, instead of wired into the network op itself.
 */
const raceWithAbort = <T>(shared: Promise<T>, signal?: AbortSignal): Promise<T> => {
  if (!signal) return shared;

  return new Promise<T>((resolve, reject) => {
    const onAbort = (): void => {
      const error = new Error('Request was cancelled');
      error.name = 'AbortError';
      reject(error);
    };

    if (signal.aborted) {
      onAbort();
      return;
    }

    signal.addEventListener('abort', onAbort, { once: true });

    shared.then(
      (value) => {
        signal.removeEventListener('abort', onAbort);
        resolve(value);
      },
      (err) => {
        signal.removeEventListener('abort', onAbort);
        reject(err);
      }
    );
  });
};

/**
 * Minimum job level for search results. Employees at this level and above are included.
 * Level 0 and null (contractors) are also included — see isJobLevelIncluded.
 */
export const DEFAULT_MIN_JOB_LEVEL = 4;

/**
 * Worker subtype codes to include in search results.
 * Includes only blue badge Amazon employees:
 *   F = Regular Full Time (blue badge, core employees)
 *   I = Intern (blue badge, on-site program participants)
 *   B = Fixed Term Contractor EU (blue badge, Amazon-employed contract workers)
 *   H = Regular Part Time - 20+ Hours
 *   R = Regular Reduced Time 30+ Hours
 *   Q = Part Time - 20-29 Hours
 *
 * Excludes:
 *   T — Onsite Vendor (yellow badge, OSP/facilities staff e.g. janitorial, security)
 *   W — 3P Onsite Worker (green badge, third-party facilities staff)
 *   X, S, M — seasonal, reduced-time, flex (FC/warehouse workers)
 *   V — offsite vendor (not on premises)
 *   C — generic 3P contractor (no guarantee of on-site presence)
 *   A, P, N — apprentices/trainees (EU programs)
 *   D, J, UNKNOWN — legacy/not vended/unclassified
 */
export const DEFAULT_WORKER_SUBTYPES = ['F', 'I', 'B', 'H', 'R', 'Q'];

/**
 * Returns true if the employee's job level should be included in results.
 * Includes: level >= minJobLevel, level 0, or null/missing (contractors).
 */
export const isJobLevelIncluded = (jobLevel?: string, minJobLevel: number = DEFAULT_MIN_JOB_LEVEL): boolean => {
  if (!jobLevel) return true;
  const level = parseInt(jobLevel, 10);
  if (isNaN(level)) return true;
  // Scholars are level 0
  return level === 0 || level >= minJobLevel;
};

/**
 * Search employees using the FINTECH_PAPI /v1/search/advanced endpoint.
 * Returns raw PAPI response with employee data.
 *
 * Includes employees at Level 4+, Level 0, and those with null jobLevel (contractors).
 * Uses includeWorkerSubtypes to filter by worker type server-side, then applies
 * job level filtering client-side.
 *
 * @param searchQuery - The search query string (minimum 2 characters)
 * @param maxResults - Maximum number of results to return (default: 10)
 * @param nextToken - Optional token for pagination
 * @returns A promise that resolves to the raw FINTECH_PAPI response
 */
export const searchEmployeesFromFintechPapi = async (
  searchQuery: string,
  maxResults: number = 10,
  nextToken?: string,
  filterOptions?: FintechPapiSearchFilterOptions
): Promise<FintechPapiResponse> => {
  try {
    const workerSubtypes = filterOptions?.includeWorkerSubtypes ?? DEFAULT_WORKER_SUBTYPES;
    const minJobLevel = filterOptions?.minJobLevel ?? DEFAULT_MIN_JOB_LEVEL;
    const path = `/v1/search/advanced`;
    const requestBody: SearchRequestBody = {
      name: searchQuery.toLowerCase(),
      activeOnly: true,
      includeWorkerSubtypes: workerSubtypes,
      size: maxResults
    };

    if (nextToken) {
      requestBody.nextToken = nextToken;
    }

    const response = await post({
      apiName: 'FINTECH_PAPI',
      path,
      options: {
        body: requestBody as any,
        queryParams: {
          expand: 'job'
        }
      }
    }).response;

    const responseBody = (await response.body.json()) as FintechPapiResponse;

    // Client-side job level filter: include L4+, L0, and null (contractors).
    // PAPI range queries exclude null jobLevel values, so we filter here instead.
    if (responseBody.employees) {
      responseBody.employees = responseBody.employees.filter((emp) => isJobLevelIncluded(emp.job?.jobLevel, minJobLevel));
    }

    return responseBody;
  } catch (error) {
    logger.warn('FINTECH_PAPI search failed', { error, searchQuery });
    return {
      employees: [],
      totalCount: 0,
      hasMore: false
    };
  }
};

/**
 * Get employee by legacy employee ID using the FINTECH_PAPI endpoint.
 * Returns raw PAPI response with employee data.
 *
 * @param legacyEmployeeId - The legacy employee ID (e.g., "xxxxx")
 * @returns A promise that resolves to the raw FINTECH_PAPI employee response
 */
export const getEmployeeByLegacyId = async (legacyEmployeeId: string): Promise<FintechPapiEmployeeResponse> => {
  try {
    const path = `/v1/employee/employee-id/${legacyEmployeeId}`;

    const response = await get({
      apiName: 'FINTECH_PAPI',
      path
    }).response;

    const responseBody = await response.body.json();
    return responseBody as FintechPapiEmployeeResponse;
  } catch (error) {
    logger.warn('FINTECH_PAPI getEmployeeByLegacyId failed', { error, legacyEmployeeId });
    return {
      employee: undefined
    };
  }
};

/**
 * Get employee by UUID employee ID using the FINTECH_PAPI endpoint.
 * Returns raw PAPI response with employee data.
 *
 * @param employeeId - The legacy employee ID (e.g., "xxxxxx-xxxx-xxx-xxx-xxxxxxxxxx")
 * @returns A promise that resolves to the raw FINTECH_PAPI employee response
 */
export const getEmployeeById = async (employeeId: string): Promise<FintechPapiEmployeeResponse> => {
  try {
    const path = `/v1/employee/${employeeId}`;

    const response = await get({
      apiName: 'FINTECH_PAPI',
      path,
      options: {
        queryParams: {
          expand: 'job'
        }
      }
    }).response;

    const responseBody = await response.body.json();
    return responseBody as FintechPapiEmployeeResponse;
  } catch (error) {
    logger.warn('FINTECH_PAPI getEmployeeById failed', { error, employeeId });
    return {
      employee: undefined
    };
  }
};

/** In-flight request deduplication — concurrent calls for the same login+expand share one network request. */
const inflightRequests = new Map<string, Promise<FintechPapiEmployeeResponse>>();

/** Short-lived response cache (30s TTL) — prevents re-fetching across StrictMode remounts and sequential useEffects. */
const responseCache = new Map<string, { data: FintechPapiEmployeeResponse; expiry: number }>();
const CACHE_TTL_MS = 30_000;

/**
 * Get employee by alias using the FINTECH_PAPI endpoint.
 * Returns raw PAPI response with employee data.
 * Deduplicates concurrent requests for the same login+expand combination.
 *
 * @param login - Alias of an employee (e.g., "johndoe")
 * @param expand - Optional expand parameter (e.g., 'job', 'direct-reports', 'job,direct-reports')
 * @param signal - Optional AbortSignal for request cancellation
 * @returns A promise that resolves to the raw FINTECH_PAPI employee response
 */
export const getEmployeeByLogin = async (login: string, expand: string = 'job', signal?: AbortSignal): Promise<FintechPapiEmployeeResponse> => {
  // Respect already-aborted signals before checking cache
  checkAbortSignal(signal);

  const cacheKey = `${login.toLowerCase()}:${expand}`;

  // Return cached response if still fresh
  const cached = responseCache.get(cacheKey);
  if (cached && Date.now() < cached.expiry) return cached.data;

  // Reuse an existing in-flight SHARED request if one exists for the same key. The shared
  // request is signal-agnostic and only ever RESOLVES (never rejects with AbortError), so we
  // race it against THIS caller's signal only — one caller aborting can't poison the others.
  const existing = inflightRequests.get(cacheKey);
  if (existing) {
    return raceWithAbort(existing, signal);
  }

  // Create the SHARED request. It intentionally ignores every caller's signal: a deduped
  // request must not be cancellable by any single participant. It runs to completion,
  // populates the cache, and resolves to real data or { employee: undefined } on failure.
  const shared = (async (): Promise<FintechPapiEmployeeResponse> => {
    try {
      const path = `/v1/employee/login/${login.toLowerCase()}`;

      const apiOperation = get({
        apiName: 'FINTECH_PAPI',
        path,
        options: {
          queryParams: {
            expand
          }
        }
      });

      const response = await apiOperation.response;
      const responseBody = await response.body.json();
      const result = responseBody as FintechPapiEmployeeResponse;

      // Cache successful responses for 30s
      if (result.employee) {
        responseCache.set(cacheKey, { data: result, expiry: Date.now() + CACHE_TTL_MS });
      }

      return result;
    } catch (error) {
      logger.warn('FINTECH_PAPI getEmployeeByLogin failed', { error, login });
      return { employee: undefined };
    } finally {
      inflightRequests.delete(cacheKey);
    }
  })();

  inflightRequests.set(cacheKey, shared);

  // The creating caller also races the shared request against its own signal, so it can bail
  // on abort without cancelling the shared op out from under other sharers.
  return raceWithAbort(shared, signal);
};

/**
 * Counts all reports under a leader recursively using PAPI direct-reports endpoint.
 * Useful for fail-fast validation before fetching all employee data.
 *
 * @param login - The leader's login alias
 * @param signal - Optional AbortSignal for request cancellation
 * @returns Total count of all reports (direct + indirect) under the leader
 */
export const countAllReportsUnderLeader = async (login: string, signal?: AbortSignal): Promise<number> => {
  try {
    // Check if cancelled before starting
    checkAbortSignal(signal);

    const response = await getEmployeeByLogin(login, 'job,direct-reports', signal);
    const employee = response.employee;

    if (!employee || !employee.directReports || employee.directReports.length === 0) {
      return 0;
    }

    let totalCount = employee.directReports.length;

    // Check if cancelled before recursive calls
    checkAbortSignal(signal);

    // Recursively count reports for managers among direct reports
    const managerReports = employee.directReports.filter((report: FintechPapiPersonInfo) => report.isManager);

    const childCounts = await Promise.all(
      managerReports.map((manager: FintechPapiPersonInfo) => (manager.login ? countAllReportsUnderLeader(manager.login, signal) : Promise.resolve(0)))
    );

    totalCount += childCounts.reduce((sum, count) => sum + count, 0);

    return totalCount;
  } catch (error) {
    // Re-throw abort errors
    if (error instanceof Error && error.name === 'AbortError') {
      throw error;
    }
    logger.error('Failed to count all reports under leader', { error, login });
    throw error;
  }
};

/**
 * Fetches all employee logins under a leader recursively using PAPI direct-reports endpoint.
 * Returns a flat array of all employee logins (excluding the leader).
 *
 * @param login - The leader's login alias
 * @param signal - Optional AbortSignal for request cancellation
 * @returns Array of all employee logins under the leader
 */
export const fetchAllReportsRecursively = async (login: string, signal?: AbortSignal): Promise<string[]> => {
  try {
    // Check if cancelled before starting
    checkAbortSignal(signal);

    const response = await getEmployeeByLogin(login, 'job,direct-reports', signal);
    const employee = response.employee;

    if (!employee || !employee.directReports || employee.directReports.length === 0) {
      return [];
    }

    const directReportLogins = employee.directReports
      .map((report: FintechPapiPersonInfo) => report.login)
      .filter((reportLogin: string | undefined): reportLogin is string => !!reportLogin);

    // Check if cancelled before recursive calls
    checkAbortSignal(signal);

    // Recursively fetch reports for managers among direct reports
    const managerReports = employee.directReports.filter((report: FintechPapiPersonInfo) => report.isManager);

    const childReports = await Promise.all(
      managerReports.map((manager: FintechPapiPersonInfo) =>
        manager.login ? fetchAllReportsRecursively(manager.login, signal) : Promise.resolve([])
      )
    );

    const allChildLogins = childReports.flat();

    return [...directReportLogins, ...allChildLogins];
  } catch (error) {
    // Re-throw abort errors
    if (error instanceof Error && error.name === 'AbortError') {
      throw error;
    }
    logger.error('Failed to fetch all reports under leader recursively', { error, login });
    throw error;
  }
};

/**
 * Fetches direct reports for a leader using PAPI direct-reports endpoint.
 * Returns only the immediate direct reports (no recursion).
 *
 * @param login - The leader's login alias
 * @param signal - Optional AbortSignal for request cancellation
 * @returns Array of direct report logins
 */
export const fetchDirectReports = async (login: string, signal?: AbortSignal): Promise<string[]> => {
  try {
    // Check if cancelled before starting
    checkAbortSignal(signal);

    const response = await getEmployeeByLogin(login, 'job,direct-reports', signal);
    const employee = response.employee;

    if (!employee || !employee.directReports || employee.directReports.length === 0) {
      return [];
    }

    return employee.directReports
      .map((report: FintechPapiPersonInfo) => report.login)
      .filter((reportLogin: string | undefined): reportLogin is string => !!reportLogin);
  } catch (error) {
    // Re-throw abort errors
    if (error instanceof Error && error.name === 'AbortError') {
      throw error;
    }
    logger.error('Failed to fetch direct reports', { error, login });
    throw error;
  }
};
